import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
    Grid,
    GridColumn as Column,
    GridToolbar,
} from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { LoadingPanel } from "../LoadingPanel";
import { addNotificationWithTimeout } from '../../app/addNotificationWithTimeout';
import { handleResponse } from '../../app/handleResponse';
import { CommandCell } from '../forms/CommandCell';
import { getCSRFToken } from '../../app/getCSRFToken';
import { Dialog } from "@progress/kendo-react-dialogs";
import { TextArea } from "@progress/kendo-react-inputs";

export const AdministratorUserAgents = () => {
    const dispatch = useDispatch();

    const [userAgents, setUserAgents] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadingError, setLoadingError] = useState("");
    const [showDialog, setShowDialog] = useState(false);
    const [newUserAgentsText, setNewUserAgentsText] = useState("");

    const getUserAgents = () => {
        setIsLoading(true);

        fetch(`/api/administrator/useragents`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify({}),
            })
            .then(handleResponse)
            .then(result => {
                setIsLoading(false);
                setUserAgents(result.userAgents);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setIsLoading(false);
                setLoadingError(error.message);
            });
    }

    const addUserAgents = (newUserAgents) => {
        const dataItem = {
            userAgents: newUserAgents,
        };

        fetch("/api/administrator/useragents",
            {
                method: "PATCH",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                setShowDialog(false);
                setNewUserAgentsText("");

                addNotificationWithTimeout(dispatch, "success", "Created items successfully");

                getUserAgents();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const deleteUserAgent = (userAgentKey) => {
        const dataItem = {
            "userAgentKey": userAgentKey
        }

        fetch("/api/administrator/useragent",
            {
                method: "DELETE",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Deleted item successfully");

                getUserAgents();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const MyCommandCell = (props) => (
        <CommandCell
            {...props}
            canEdit={false}
            remove={remove}
            keyField="userAgentKey"
        />
    );

    const remove = (dataItem) => {
        deleteUserAgent(dataItem.userAgentKey);
    };

    const addNew = () => {
        //Change the new user agents text to an array
        if (newUserAgentsText.trim().length === 0) {
            addNotificationWithTimeout(dispatch, "error", "No user agents entered to save");
            return;
        }

        const newUserAgents = newUserAgentsText
            .split("\n")
            .filter(word => word.trim().length > 0);

        if (newUserAgents.length === 0) {
            addNotificationWithTimeout(dispatch, "error", "No user agents entered to save");
            return;
        }

        addUserAgents(newUserAgents);
    };

    const deleteAll = () => {
        if (window.confirm("Are you sure you want to delete all of the user agents?") === false) {
            return;
        }

        fetch("/api/administrator/useragents",
            {
                method: "DELETE",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify({}),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", `Deleted ${result.itemsDeletedCount} user agents successfully`);

                getUserAgents();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    };

    useEffect(() => {
        document.title = "User Agents";

        getUserAgents();
    }, []);

    return (
        <>
            { isLoading ? <LoadingPanel /> : (loadingError === "" ? (
                <div>
                    <Grid
                        data={userAgents}
                        dataItemKey={"userAgentKey"}
                        resizable={true}
                        scrollable="none"
                    >
                        <GridToolbar>
                            <Button
                                title={"Add new user agents"}
                                themeColor={"primary"}
                                icon="plus"
                                onClick={() => setShowDialog(true)}
                            >
                                Add new user agents
                            </Button>

                            <Button
                                title={"Delete all user agents"}                               
                                icon="delete"
                                onClick={deleteAll}
                            >
                                Delete all user agents
                            </Button>
                        </GridToolbar>
                        <Column cell={MyCommandCell} />
                        <Column field="value" title={"User agent"} />                     
                    </Grid>
                </div>
                ) : <div></div>)
            }

            {showDialog === false ? <></> :
                <Dialog
                    title={"User Agents (1 user agent per line)"}
                    onClose={() => setShowDialog(false)}
                    width={"90vw"}
                >
                    <div className="mb-3">
                        <TextArea
                            rows={5}
                            value={newUserAgentsText} 
                            onChange={(e) => setNewUserAgentsText(e.value)}
                        />
                    </div>
                    <div>
                        <Button
                            title={"Save"}
                            themeColor={"primary"}
                            icon="save"
                            onClick={addNew}
                        >
                            Save
                        </Button>
                    </div> 
                </Dialog>
            }
        </>
    );
}